import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import TopicContentViewerModal from './TopicContentViewerModal';
import { TrainingQuizWizard } from './TrainingQuizWizard';
import type {
    TrainingPlan,
    TrainingSchedule,
    TrainingCompletion,
    TrainingEnrollment,
    PlanCategory,
    TrainingQuizResponse,
} from '../../types/trainingTypes';
import { TOPIC_STYLE_COLORS } from '../../types/trainingTypes';
import {
    calculateDayNumberInCycle,
    parseLocalDateStr,
    checkQuizEligibility,
    getCategoryAccumulatedMinutes,
} from '../../utils/trainingUtils';
import { listTrainingQuizResponsesByUser } from '../../graphql/trainingQueries';
import '../../css/DailyTrainingPopup.css';

interface DailyTrainingPopupProps {
    onClose: () => void;
}

const STYLE_ICONS: Record<string, string> = {
    Video: '🎬',
    PDF: '📄',
    Poster: '🖼️',
    FatalGram: '⚠️',
    Other: '📋',
    Uncategorized: '📎',
};

/**
 * Daily training popup — shows today's topic with a button to open
 * the TopicContentViewerModal (which handles viewing + signature + completion).
 *
 * Validates: Requirements 16.1–16.16
 */
const DailyTrainingPopup = ({ onClose }: DailyTrainingPopupProps) => {
    const {
        plans,
        topics,
        schedules,
        enrollments,
        completions,
        recordCompletion,
    } = useSafetyTrainingContext();

    const { companyUser, companyOwner } = useUserContext();

    const currentUserId = companyUser?.id ?? companyOwner?.id ?? '';
    const companyId = (companyUser?.companyId as string) ?? companyOwner?.companyId ?? '';

    const [showContentViewer, setShowContentViewer] = useState(false);
    const [justCompleted, setJustCompleted] = useState(false);
    const [confirmedAt, setConfirmedAt] = useState<Date | null>(null);
    const [currentTopicIndex, setCurrentTopicIndex] = useState(0);

    // Quiz state
    const [showQuizWizard, setShowQuizWizard] = useState(false);
    const [quizCategory, setQuizCategory] = useState<PlanCategory | null>(null);
    const [quizResponses, setQuizResponses] = useState<TrainingQuizResponse[]>([]);
    const [quizResponsesLoaded, setQuizResponsesLoaded] = useState(false);

    // Timezone-safe "today" (design decision #10)
    const todayLocal = useMemo(() => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d = String(now.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }, []);

    const todayDisplay = useMemo(
        () =>
            new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            }),
        [],
    );

    // User enrollment & active plan
    const userEnrollments = useMemo<TrainingEnrollment[]>(
        () => enrollments.filter(e => e.userId === currentUserId),
        [enrollments, currentUserId],
    );

    const activePlan = useMemo<TrainingPlan | null>(() => {
        const enrolledPlanIds = new Set(userEnrollments.map(e => e.planId));
        return plans.find(p => p.status === 'ACTIVE' && enrolledPlanIds.has(p.id)) ?? null;
    }, [plans, userEnrollments]);

    const activeEnrollment = useMemo<TrainingEnrollment | null>(() => {
        if (!activePlan) return null;
        return userEnrollments.find(e => e.planId === activePlan.id) ?? null;
    }, [activePlan, userEnrollments]);

    const categories = useMemo<PlanCategory[]>(() => {
        if (!activePlan) return [];
        try {
            return JSON.parse(activePlan.categories) as PlanCategory[];
        } catch {
            return [];
        }
    }, [activePlan]);

    const dayNumber = useMemo(() => {
        if (!activeEnrollment || !activePlan) return 0;
        const startDate = parseLocalDateStr(activeEnrollment.enrolledDate);
        return calculateDayNumberInCycle(startDate, new Date(), activePlan.cycleDuration);
    }, [activeEnrollment, activePlan]);

    // Today's schedule & topic
    const planSchedules = useMemo<TrainingSchedule[]>(() => {
        if (!activePlan) return [];
        return schedules.filter(s => s.planId === activePlan.id);
    }, [schedules, activePlan]);

    // Today's schedules — support multiple topics per day, prioritized by:
    // 1. Rescheduled items (higher dayNumber relative to plan start) first
    // 2. Then by dayNumber ascending
    // Only show entries assigned to everyone (null) or to this specific user
    const todaySchedules = useMemo<TrainingSchedule[]>(() => {
        const todays = planSchedules.filter(s => {
            if (s.scheduledDate.split('T')[0] !== todayLocal) return false;
            // Filter by assignedUserId: null = everyone, or matches current user
            return !s.assignedUserId || s.assignedUserId === currentUserId;
        });
        // Sort: rescheduled items (those with a dayNumber that doesn't match their position) first,
        // then by dayNumber ascending
        return todays.sort((a, b) => {
            // Items with locked=false (rescheduled) come first
            const aRescheduled = a.locked === false ? 0 : 1;
            const bRescheduled = b.locked === false ? 0 : 1;
            if (aRescheduled !== bRescheduled) return aRescheduled - bRescheduled;
            return a.dayNumber - b.dayNumber;
        });
    }, [planSchedules, todayLocal]);

    // Current schedule entry (based on index)
    const todaySchedule = useMemo<TrainingSchedule | null>(() => {
        return todaySchedules[currentTopicIndex] ?? null;
    }, [todaySchedules, currentTopicIndex]);

    const todayTopic = useMemo(() => {
        if (!todaySchedule) return null;
        return topics.find(
            t => t.topicId === todaySchedule.topicId || t.id === todaySchedule.topicId,
        ) ?? null;
    }, [todaySchedule, topics]);

    const todayCategory = useMemo(() => {
        if (!todaySchedule?.categoryId) return null;
        return categories.find(c => c.id === todaySchedule.categoryId) ?? null;
    }, [todaySchedule, categories]);

    // Check if already completed today
    const userCompletions = useMemo<TrainingCompletion[]>(() => {
        if (!activePlan) return [];
        return completions.filter(
            c => c.userId === currentUserId && c.planId === activePlan.id,
        );
    }, [completions, currentUserId, activePlan]);

    const todayCompletion = useMemo<TrainingCompletion | null>(() => {
        if (!todaySchedule) return null;
        return userCompletions.find(c =>
            c.scheduleDate.split('T')[0] === todayLocal &&
            (c.topicId === todaySchedule.topicId)
        ) ?? null;
    }, [userCompletions, todaySchedule, todayLocal]);

    // Track completions for all today's schedules
    const todayCompletedTopicIds = useMemo(() => {
        return new Set(
            userCompletions
                .filter(c => c.scheduleDate.split('T')[0] === todayLocal)
                .map(c => c.topicId)
        );
    }, [userCompletions, todayLocal]);

    // Count of remaining (uncompleted) topics for today
    const remainingTodayCount = useMemo(() => {
        return todaySchedules.filter(s => !todayCompletedTopicIds.has(s.topicId)).length;
    }, [todaySchedules, todayCompletedTopicIds]);

    // On mount / data load, skip to the first uncompleted topic
    useEffect(() => {
        const firstUncompleted = todaySchedules.findIndex(s => !todayCompletedTopicIds.has(s.topicId));
        if (firstUncompleted >= 0 && firstUncompleted !== currentTopicIndex) {
            setCurrentTopicIndex(firstUncompleted);
        }
    }, [todaySchedules, todayCompletedTopicIds]);

    // Progress
    const totalMinutesViewed = useMemo(
        () => userCompletions.reduce((sum, c) => sum + c.timeValueMinutes, 0),
        [userCompletions],
    );

    const percentViewed = useMemo(() => {
        if (!activePlan || activePlan.minimumTimeRequired === 0) return 0;
        return Math.min(100, Math.round((totalMinutesViewed / activePlan.minimumTimeRequired) * 100));
    }, [activePlan, totalMinutesViewed]);

    const confirmedPercent = useMemo(() => {
        if (!activePlan || activePlan.minimumTimeRequired === 0) return 0;
        const added = todayTopic?.timeValue ?? 0;
        return Math.min(100, Math.round(((totalMinutesViewed + added) / activePlan.minimumTimeRequired) * 100));
    }, [activePlan, totalMinutesViewed, todayTopic]);

    const dueDateStr = useMemo(() => {
        if (!activeEnrollment || !activePlan) return '—';
        const dueDate = activeEnrollment.nextDueDate
            ? parseLocalDateStr(activeEnrollment.nextDueDate)
            : (() => {
                const enrolled = parseLocalDateStr(activeEnrollment.enrolledDate);
                const due = new Date(enrolled);
                due.setDate(due.getDate() + activePlan.cycleDuration);
                return due;
            })();
        return dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }, [activeEnrollment, activePlan]);

    // Determine if today's topic triggers a quiz (calculated before user views content)
    const todayQuizCategory = useMemo<PlanCategory | null>(() => {
        if (!todaySchedule?.categoryId || !activePlan || !activeEnrollment) return null;
        const cat = categories.find(c => c.id === todaySchedule.categoryId);
        if (!cat?.requiresQuiz || !cat.quizFormId) return null;

        // Calculate accumulated minutes INCLUDING this topic (since they're about to complete it)
        const categoryCompletions = userCompletions.filter(c => c.categoryId === cat.id);
        const accumulatedMinutes = getCategoryAccumulatedMinutes(cat.id, categoryCompletions) + (todayTopic?.timeValue ?? 0);

        if (accumulatedMinutes < cat.targetMinutes) return null;

        // Check eligibility from already-loaded quiz responses
        if (!quizResponsesLoaded) return null;
        const cycleStart = activeEnrollment.enrolledDate;
        const categoryResponses = quizResponses.filter(
            (r: TrainingQuizResponse) => r.planId === activePlan.id && r.categoryId === cat.id && r.cycleStartDate === cycleStart
        );
        const eligibility = checkQuizEligibility(cat, accumulatedMinutes, categoryResponses);
        return eligibility === 'quiz-due' ? cat : null;
    }, [todaySchedule, activePlan, activeEnrollment, categories, userCompletions, todayTopic, quizResponses, quizResponsesLoaded]);

    // Handle completion from TopicContentViewerModal (with signature)
    // The modal now handles both the completion signature AND the quiz submission in one flow
    const handleMarkAsViewed = useCallback(async (signaturePath: string) => {
        if (!todaySchedule || !activePlan || todayCompletion) return;
        try {
            await recordCompletion({
                companyId,
                userId: currentUserId,
                topicId: todaySchedule.topicId,
                planId: activePlan.id,
                scheduleDate: todaySchedule.scheduledDate,
                completionDate: new Date().toISOString(),
                launched: true,
                markedAsViewed: true,
                timeValueMinutes: todayTopic?.timeValue ?? 0,
                categoryId: todaySchedule.categoryId ?? null,
                signaturePath,
            });
            setJustCompleted(true);
            setConfirmedAt(new Date());
        } catch (err) {
            console.error('Failed to record completion:', err);
        }
    }, [
        todaySchedule, activePlan, todayCompletion, companyId,
        currentUserId, todayTopic, recordCompletion,
    ]);

    // Called by TopicContentViewerModal after quiz response is saved
    const handleQuizSubmitted = useCallback((_passed: boolean) => {
        // Remove from pending quizzes since it's been taken
        if (todayQuizCategory) {
            setPendingQuizCategories(prev => prev.filter(c => c.id !== todayQuizCategory.id));
        }
    }, [todayQuizCategory]);

    // Advance to next uncompleted topic
    const handleNextTopic = useCallback(() => {
        setJustCompleted(false);
        setConfirmedAt(null);
        setShowContentViewer(false);
        // Find next uncompleted topic
        for (let i = currentTopicIndex + 1; i < todaySchedules.length; i++) {
            if (!todayCompletedTopicIds.has(todaySchedules[i].topicId)) {
                setCurrentTopicIndex(i);
                return;
            }
        }
        // If no more, close
        onClose();
    }, [currentTopicIndex, todaySchedules, todayCompletedTopicIds, onClose]);

    // Don't render if no topic scheduled for today AND no pending quiz
    // Check for pending quizzes on mount
    const [pendingQuizCategories, setPendingQuizCategories] = useState<PlanCategory[]>([]);

    useEffect(() => {
        const checkPendingQuizzes = async () => {
            if (!activePlan || !activeEnrollment || categories.length === 0) return;

            try {
                const responses = await listTrainingQuizResponsesByUser(currentUserId);
                setQuizResponses(responses);
                setQuizResponsesLoaded(true);

                const cycleStart = activeEnrollment.enrolledDate;
                const pending: PlanCategory[] = [];

                for (const cat of categories) {
                    if (!cat.requiresQuiz || !cat.quizFormId) continue;

                    const accMinutes = getCategoryAccumulatedMinutes(cat.id, userCompletions);
                    if (accMinutes < cat.targetMinutes) continue;

                    const categoryResponses = responses.filter(
                        (r: TrainingQuizResponse) => r.planId === activePlan.id && r.categoryId === cat.id && r.cycleStartDate === cycleStart
                    );

                    const eligibility = checkQuizEligibility(cat, accMinutes, categoryResponses);
                    if (eligibility === 'quiz-due') {
                        pending.push(cat);
                    }
                }

                setPendingQuizCategories(pending);
            } catch (err) {
                console.error('Error checking pending quizzes:', err);
            }
        };

        checkPendingQuizzes();
    }, [activePlan, activeEnrollment, categories, currentUserId, userCompletions]);

    const hasPendingQuiz = pendingQuizCategories.length > 0;

    // Don't render if no topic scheduled AND no pending quiz
    if ((todaySchedules.length === 0 || !todayTopic || !activePlan) && !hasPendingQuiz) return null;

    // If all today's topics are completed and no pending quiz and not just-completed, don't show
    if (remainingTodayCount === 0 && !justCompleted && !hasPendingQuiz && !showQuizWizard) return null;

    const styleColor = todayTopic ? (TOPIC_STYLE_COLORS[todayTopic.style] ?? '#6b7280') : '#6b7280';
    const styleIcon = todayTopic ? (STYLE_ICONS[todayTopic.style] ?? '📋') : '📝';

    return (
        <div className="dtp-overlay" onClick={onClose}>
            {!showQuizWizard && (
                <div
                    className={`dtp-popup ${justCompleted ? 'confirmed' : ''}`}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="dtp-header">
                        <div className="dtp-header-top">
                            <h2>TODAY'S TRAINING</h2>
                            <button className="dtp-close-btn" onClick={onClose} aria-label="Close">×</button>
                        </div>
                        <div className="dtp-header-date">
                            {todayDisplay}
                            {dayNumber > 0 && activePlan && (
                                <span className="dtp-day-badge">Day {dayNumber} of {activePlan.cycleDuration}</span>
                            )}
                        </div>
                    </div>

                    {/* Body */}
                    <div className="dtp-body">
                        {/* Confirmed Banner */}
                        {justCompleted && confirmedAt && (
                            <div className="dtp-confirmed-banner">
                                <div className="dtp-confirmed-icon">✅</div>
                                <div className="dtp-confirmed-text">
                                    Recorded — {confirmedAt.toLocaleDateString('en-US', {
                                        month: 'short', day: 'numeric', year: 'numeric',
                                    })} at {confirmedAt.toLocaleTimeString('en-US', {
                                        hour: 'numeric', minute: '2-digit',
                                    })}
                                </div>
                                <div className="dtp-confirmed-detail">
                                    +{todayTopic?.timeValue ?? 0} min added to {todayCategory?.name ?? 'training'}
                                </div>
                            </div>
                        )}

                        {/* Topic Display */}
                        {todayTopic ? (
                            <div className="dtp-topic">
                                <div
                                    className="dtp-topic-icon"
                                    style={{ background: styleColor + '20', color: styleColor }}
                                >
                                    {styleIcon}
                                </div>
                                <div className="dtp-topic-info">
                                    <h3 className="dtp-topic-title">{todayTopic.description}</h3>
                                    <div className="dtp-topic-meta">
                                        <span className="dtp-meta-tag">
                                            <span className="dtp-style-dot" style={{ background: styleColor }} />
                                            {todayTopic.style}
                                        </span>
                                        <span className="dtp-meta-tag">ID: {todayTopic.topicId}</span>
                                        <span className="dtp-meta-tag">{todayTopic.timeValue} min</span>
                                        {todayCategory && (
                                            <span className="dtp-meta-tag">{todayCategory.name}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : hasPendingQuiz ? (
                            <div className="dtp-topic">
                                <div className="dtp-topic-icon" style={{ background: '#fef3c720', color: '#d97706' }}>
                                    📝
                                </div>
                                <div className="dtp-topic-info">
                                    <h3 className="dtp-topic-title">Completion Quiz Available</h3>
                                    <div className="dtp-topic-meta">
                                        <span className="dtp-meta-tag">{pendingQuizCategories[0].name}</span>
                                        <span className="dtp-meta-tag">
                                            {pendingQuizCategories.length} quiz{pendingQuizCategories.length > 1 ? 'zes' : ''} pending
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {/* Single action button */}
                        <div className="dtp-actions">
                            {justCompleted ? (
                                remainingTodayCount > 0 ? (
                                    <button className="dtp-btn-launch" onClick={handleNextTopic}>
                                        Next Topic ({remainingTodayCount} remaining)
                                    </button>
                                ) : hasPendingQuiz ? (
                                    <button className="dtp-btn-launch" onClick={() => {
                                        setQuizCategory(pendingQuizCategories[0]);
                                        setShowQuizWizard(true);
                                    }}>
                                        📝 Take Completion Quiz: {pendingQuizCategories[0].name}
                                    </button>
                                ) : (
                                    <button className="dtp-btn-close" onClick={onClose}>
                                        Close
                                    </button>
                                )
                            ) : hasPendingQuiz && (!todayTopic || remainingTodayCount === 0) ? (
                                <button className="dtp-btn-launch" onClick={() => {
                                    setQuizCategory(pendingQuizCategories[0]);
                                    setShowQuizWizard(true);
                                }}>
                                    📝 Take Completion Quiz: {pendingQuizCategories[0].name}
                                </button>
                            ) : (
                                <button
                                    className="dtp-btn-launch"
                                    onClick={() => setShowContentViewer(true)}
                                >
                                    View Today's Training
                                </button>
                            )}
                        </div>

                        {/* Pending quiz indicator */}
                        {hasPendingQuiz && todayTopic && remainingTodayCount > 0 && (
                            <div className="dtp-multi-topic-indicator" style={{ marginTop: '8px' }}>
                                📝 You have {pendingQuizCategories.length} pending quiz{pendingQuizCategories.length > 1 ? 'zes' : ''} after today's training
                            </div>
                        )}

                        {/* Multi-topic indicator */}
                        {todaySchedules.length > 1 && (
                            <div className="dtp-multi-topic-indicator">
                                {justCompleted
                                    ? `Completed ${todaySchedules.length - remainingTodayCount} of ${todaySchedules.length} topics for today`
                                    : `Topic ${currentTopicIndex + 1} of ${todaySchedules.length} scheduled for today`
                                }
                            </div>
                        )}

                        {/* Progress Bar */}
                        {activePlan && (
                            <div className="dtp-progress">
                                <div className="dtp-progress-header">
                                    <span>
                                        {justCompleted ? confirmedPercent : percentViewed}% complete
                                    </span>
                                    <span>
                                        {justCompleted
                                            ? `${totalMinutesViewed + (todayTopic?.timeValue ?? 0)}/${activePlan.minimumTimeRequired} min`
                                            : `${totalMinutesViewed}/${activePlan.minimumTimeRequired} min`
                                        }
                                    </span>
                                </div>
                                <div className="dtp-progress-track">
                                    <div
                                        className={`dtp-progress-fill ${justCompleted ? 'green' : ''}`}
                                        style={{
                                            width: `${justCompleted ? confirmedPercent : percentViewed}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Remind me later */}
                        {!justCompleted && (
                            <span className="dtp-link" onClick={onClose}>Remind me later</span>
                        )}
                    </div>

                    {/* Plan Footer */}
                    {!justCompleted && activePlan && (
                        <div className="dtp-plan-footer">
                            <span>{activePlan.name}</span>
                            <span>Due: {dueDateStr}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Content Viewer Modal with signature flow — quiz is embedded */}
            {todayTopic && (
                <TopicContentViewerModal
                    isOpen={showContentViewer}
                    onClose={() => setShowContentViewer(false)}
                    topic={todayTopic}
                    onMarkAsViewed={todayCompletion || justCompleted ? undefined : handleMarkAsViewed}
                    companyId={companyId}
                    quizCategory={todayQuizCategory}
                    quizPlanId={activePlan?.id}
                    quizUserId={currentUserId}
                    quizCycleStartDate={activeEnrollment?.enrolledDate}
                    quizExistingAttempts={
                        todayQuizCategory && activePlan && activeEnrollment
                            ? quizResponses.filter(
                                r => r.planId === activePlan.id && r.categoryId === todayQuizCategory.id && r.cycleStartDate === activeEnrollment.enrolledDate
                            )
                            : []
                    }
                    onQuizSubmitted={handleQuizSubmitted}
                />
            )}

            {/* Training Quiz Wizard */}
            {showQuizWizard && quizCategory && activePlan && activeEnrollment && (
                <div className="quiz-wizard-overlay" onClick={e => e.stopPropagation()}>
                    <TrainingQuizWizard
                        category={quizCategory}
                        companyId={companyId}
                        userId={currentUserId}
                        planId={activePlan.id}
                        cycleStartDate={activeEnrollment.enrolledDate}
                        existingAttempts={quizResponses.filter(
                            r => r.planId === activePlan.id && r.categoryId === quizCategory.id && r.cycleStartDate === activeEnrollment.enrolledDate
                        )}
                        onComplete={(_passed) => {
                            setShowQuizWizard(false);
                            setQuizCategory(null);
                            // Remove from pending regardless of pass/fail — they've taken their attempt today
                            setPendingQuizCategories(prev => prev.filter(c => c.id !== quizCategory.id));
                        }}
                        onCancel={() => {
                            setShowQuizWizard(false);
                            setQuizCategory(null);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default DailyTrainingPopup;
